import { extInfo } from 'lib/extInfo';
import { useSelector } from 'react-redux';
import { hideDownloadItemSize, isEmptyFolder } from 'reactApp/appHelpers/appHelpers';
import { ENABLE_FULL_RESPONSIVE, IS_B2B_BIZ_USER, IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { attachesCloneLimit } from 'reactApp/appHelpers/featuresHelpers';
import { isAiOrganizeMultiSortButton } from 'reactApp/appHelpers/featuresHelpers/features/fakedoorNeuroOrganizePromo';
import { enableNewPersonalDocuments } from 'reactApp/appHelpers/featuresHelpers/features/newPersonalDocuments';
import { trashbinMoveToSafeButton } from 'reactApp/appHelpers/featuresHelpers/features/overquotaSafe';
import { seriallyClearBinFeature } from 'reactApp/appHelpers/featuresHelpers/features/seriallyClearBin';
import {
    isMobileTrashbinSelectiveActive,
    isTrashbinSelectiveActive,
} from 'reactApp/appHelpers/featuresHelpers/features/tashbinSelectiveDeletion';
import { isThemedSharedToobarItem, isThemedSharedToobarItemMore } from 'reactApp/appHelpers/featuresHelpers/features/themedPublic';
import { forceVigboLinkedAccount } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { enableRootAlbumVigboPrint } from 'reactApp/appHelpers/featuresHelpers/features/vigboPrintFakedoor';
import { BREAKPOINT_SM } from 'reactApp/constants/breakpoints';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useActionsConfig } from 'reactApp/hooks/useActionsConfig.new';
import { useWindowSize } from 'reactApp/hooks/useWindowSize';
import { getCurrentAlbum } from 'reactApp/modules/albums/albums.selector';
import { getAllDocumentsCategory } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { AllDocumentsCategoryType } from 'reactApp/modules/allDocuments/allDocuments.types';
import { getFilesWithSelectedFace } from 'reactApp/modules/faces/faces.selectors';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { isPublicUploadEnabled } from 'reactApp/modules/public/public.selectors';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import {
    getCurrentTotalCount,
    getIdsCount,
    getItemsTotalCount,
    getStorageCurrentFolder,
    getStorageSelectedItems,
    isFolderWithAuthor,
} from 'reactApp/modules/storage/storage.selectors';
import { type CloudItem, EActions, EStorageType } from 'reactApp/modules/storage/storage.types';
import { getOpenedBinCountAll } from 'reactApp/modules/trashbin/trashbin.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { getIsLetterGroup } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import { getCurrentVigboAlbum, getVigboLinkState, hasActiveVigboTariff } from 'reactApp/modules/vigbo/vigbo.selectors';
import { VigboAccountLinkState } from 'reactApp/modules/vigbo/vigbo.types';
import type { RootState } from 'reactApp/store';
import type { ListItem } from 'reactApp/ui/DropdownList/DropdownList';
import { isAiOrganizeSortBttnInToolbar } from 'reactApp/ui/FakedoorNeuroOrganizePromo/utils';
import { Move2SafeButton } from 'reactApp/ui/Toolbar/Move2SafeButton/Move2SafeButton';
import {
    actionsOrder,
    actionsOrder_EbookReader,
    actionsOrder_PdfEdit,
    moreListOrder,
    TOOLBAR_CONFIG,
} from 'reactApp/ui/Toolbar/Toolbar.config.new';
import {
    getAddToVigboAlbumIcon,
    getClearItem,
    getCloneItem,
    getDownloadItem,
    getSelectItem,
    toolbarDropdownItems,
    toolbarItems,
    toolbarStartPageItems,
} from 'reactApp/ui/Toolbar/Toolbar.data.new';
import { type ToolbarItem, type TToolbarItem, EToolbarItemTooltip, ToolbarContextEnum } from 'reactApp/ui/Toolbar/Toolbar.types';
import { isFakedoorCompleted } from 'reactApp/ui/VigboPrintFakedoor/utils';
import { sendXray } from 'reactApp/utils/ga';

const getPriority = (item) => (typeof item == 'number' ? item : undefined);

const checkAttachesCloneLimit = (storage: EStorageType, selectedCount: number) =>
    storage !== EStorageType.attaches || !attachesCloneLimit || selectedCount <= attachesCloneLimit;

// eslint-disable-next-line max-lines-per-function, complexity
export const useActions = ({
    storage,
    toolbarType,
    downloadButtonRef,
    isEbookReader,
    onDownload,
    isPublicOfOverquota,
}: {
    storage: EStorageType;
    toolbarType?: ToolbarContextEnum;
    downloadButtonRef?: React.RefObject<HTMLDivElement>;
    isEbookReader?: boolean;
    onDownload?(): void;
    isPublicOfOverquota?: boolean;
}): TToolbarItem[] => {
    const currentCategory = useSelector(getAllDocumentsCategory);
    const isIncomingItem = currentCategory === AllDocumentsCategoryType.sharedWithYou;
    const storageForToolbar = isIncomingItem ? EStorageType.incomingPublic : storage;

    const settings = TOOLBAR_CONFIG[storageForToolbar];

    const itemsCount = useSelector((state: RootState) => getIdsCount(state, storage as EStorageType));
    const selectedItems: CloudItem[] = useSelector((state: RootState) => getStorageSelectedItems(state, storage)) as CloudItem[];
    const isTotalAllSelected = useSelector(SelectionsSelectors.isTotalAllSelected);
    const filesWithFaces = useSelector(getFilesWithSelectedFace);
    const quotaCleanerIsLetterGroup = useSelector((state: RootState) => getIsLetterGroup(state));
    const currentFolder = useSelector((state: RootState) => getStorageCurrentFolder(state, storage));
    const folderItemsCount = currentFolder && 'count' in currentFolder ? currentFolder.count?.all : 0;

    const { isHome, isQuotaCleaner, isPublic, isStock, isAlbums, isDocuments, isFavorites, isTemplates, isStart, isTrashBin, isVigbo } =
        getStorage(storage);
    const isRootHome = isHome && currentFolder?.id === ROOT_FOLDER_ID;
    const totalItemsCount = useSelector((state: RootState) => getCurrentTotalCount(state, undefined, undefined));
    const selectedCount = selectedItems.length;
    const allSelected = !!selectedCount && selectedCount === totalItemsCount;
    const currentAlbum = useSelector(getCurrentAlbum);
    const isPublicUpload = useSelector(isPublicUploadEnabled);
    const folderWithAuthors = useSelector(isFolderWithAuthor);
    const isAnonymous = useSelector(UserSelectors.isAnonymous);
    const facesIsOff = !filesWithFaces.length;
    const binItemsTotalLength = useSelector((state: RootState) => getOpenedBinCountAll(state));
    const isUserInOverquota = useSelector(UserQuotaSelectors.isOverquota);
    const quotaState = useSelector(getUserOverquotaState);

    const safeBttnCondition = !IS_PHONE_BROWSER && trashbinMoveToSafeButton;
    const enableMove2SafeBttn = isUserInOverquota && safeBttnCondition;

    // tempexp_17340-start
    const currentFolderIsShared = !selectedCount && currentFolder && 'weblink' in currentFolder && currentFolder.weblink;
    const changeDesignTooltipPromo = useSelector(PromoSelectors.getPromo(EPromoType.changePublicDesign));
    // tempexp_17340-end

    const itemsAllCount = useSelector(getItemsTotalCount);

    const showAuthorsFilter = settings?.authorSort && (folderWithAuthors || (isPublic && isPublicUpload)) && facesIsOff && !isAnonymous;

    const availableActions = useActionsConfig();

    const { windowWidth } = useWindowSize();

    const isLinkedVigboAccount = useSelector(getVigboLinkState) === VigboAccountLinkState.linked || forceVigboLinkedAccount;
    const currentVigboAlbum = useSelector(getCurrentVigboAlbum);

    const isVigboActiveTariff = useSelector(hasActiveVigboTariff);
    const isVigboLinkedAccount = useSelector(getVigboLinkState) === VigboAccountLinkState.linked || forceVigboLinkedAccount;
    const vigboAlbumsExts = extInfo.getExtsForVigboAlbums();
    const isAddToVigboAlbumAvailable =
        storage !== EStorageType.albums &&
        isVigboActiveTariff &&
        isVigboLinkedAccount &&
        ((storage !== EStorageType.vigbo &&
            selectedItems.length > 0 &&
            selectedItems.every((item) => Boolean(item) && 'ext' in item && vigboAlbumsExts.includes(item.ext))) ||
            (storage === EStorageType.vigbo && currentVigboAlbum));

    if (!settings || toolbarType === ToolbarContextEnum.pageContentContext) {
        return [];
    }

    let filterHandler = (action: EActions) =>
        availableActions[action] &&
        Boolean(toolbarItems[action] || (isStart && toolbarStartPageItems[action])) &&
        Boolean(settings[action]);

    const hasActionInEmptyState = (action: EActions) => {
        let available = [EActions.selectAll];
        if (isAlbums) {
            available = [EActions.createAlbum];

            if (enableRootAlbumVigboPrint && !isFakedoorCompleted()) {
                available.push(EActions.printVigbo);
            }
        }

        if (isVigbo) {
            if (currentFolder && 'weblink' in currentFolder && currentFolder.weblink) {
                available.push(EActions.publish, EActions.addToVigboAlbum, EActions.remove);
            } else {
                available = [EActions.createVigboGallery, EActions.goToVigbo];
            }
        }

        if ((isDocuments && !enableNewPersonalDocuments) || isTemplates) {
            return false;
        }

        if (isHome && !isRootHome) {
            available.push(EActions.remove);
        }

        // CLOUD-4594: share available only for non-family folders
        if (isHome && !isRootHome && (currentFolder as any)?.kind !== 'family') {
            const isCurrentFolderRestricted = (currentFolder as any)?.kind === 'restricted';
            available = isCurrentFolderRestricted ? [...available, EActions.publish] : [...available, EActions.publish, EActions.share];

            if (availableActions[EActions.changeDesign]) {
                available.push(EActions.changeDesign);
            }
        }

        return available.includes(action);
    };

    const hasActionInEmptyStateB2B = (filterHandler) => {
        const available = [EActions.selectAll];
        return (action: EActions) => {
            return available.includes(action) || filterHandler(action);
        };
    };

    if (itemsCount === 0 && !currentAlbum && !currentVigboAlbum && !showAuthorsFilter) {
        filterHandler = IS_B2B_BIZ_USER ? hasActionInEmptyStateB2B(filterHandler) : hasActionInEmptyState;
    }

    if (isPublicOfOverquota) {
        filterHandler = (action: EActions) => {
            return [EActions.clone, EActions.download].includes(action);
        };
    }

    const actionsOrderList = isEbookReader
        ? actionsOrder_EbookReader
        : storage === EStorageType.pdfEdit
        ? actionsOrder_PdfEdit
        : actionsOrder;

    const actions: TToolbarItem[] = actionsOrderList
        .filter(filterHandler)
        // eslint-disable-next-line complexity
        .map((action) => {
            let item: TToolbarItem = toolbarItems[action] || toolbarStartPageItems[action];

            if (!isFavorites && !isStart && [EActions.addToFavorites, EActions.removeFromFavorites].includes(action)) {
                return null;
            }

            if (isTrashBin && action === EActions.clear && !enableMove2SafeBttn && safeBttnCondition && quotaState?.status === 'loaded') {
                sendXray(['safe-bttn-m2s-trash-notshown', quotaState.state ?? 'unk', isUserInOverquota ? 'isoverq' : 'notoverq']);
            }

            if (action === EActions.download) {
                const isDownloadOff = isQuotaCleaner && quotaCleanerIsLetterGroup;
                const { isIncomingPublic } = getStorage(storage);
                if (
                    isDownloadOff ||
                    (!isIncomingPublic &&
                        !isIncomingItem &&
                        !isPublicOfOverquota &&
                        ((selectedItems?.length > 0 && hideDownloadItemSize(selectedItems)) || isEmptyFolder(currentFolder)))
                ) {
                    return null;
                }

                item = getDownloadItem(
                    selectedItems,
                    filesWithFaces,
                    currentFolder as CloudItem,
                    isRootHome,
                    downloadButtonRef,
                    isEbookReader,
                    isTotalAllSelected,
                    isPublic
                );
                if (onDownload) {
                    const onClick = item.onClick;
                    item.onClick = (...args) => {
                        onDownload();
                        onClick?.(...args);
                    };
                }
            } else if (action === EActions.selectAll) {
                item = getSelectItem({
                    allSelected,
                    itemsAllCount,
                    itemsCount:
                        isTotalAllSelected && folderItemsCount && storage === EStorageType.public
                            ? folderItemsCount
                            : seriallyClearBinFeature && allSelected && storage === EStorageType.trashbin
                            ? binItemsTotalLength
                            : selectedCount,
                    disabled: itemsCount === 0 && !showAuthorsFilter,
                });
            } else if (action === EActions.clone) {
                if (!checkAttachesCloneLimit(storage, selectedCount)) {
                    return null;
                }

                item = getCloneItem(isPublic || isStock);
                // tempexp_17340-next-line
            } else if (action === EActions.changeDesign && currentFolderIsShared && isThemedSharedToobarItem) {
                item.tooltipId = changeDesignTooltipPromo ? EToolbarItemTooltip.changeDesign : undefined;
            } else if (enableMove2SafeBttn && action === EActions.clear) {
                item = { Item: Move2SafeButton as ToolbarItem };
            } else if ((isTrashbinSelectiveActive || isMobileTrashbinSelectiveActive) && action === EActions.clear) {
                item = getClearItem(selectedCount);
            }

            if (action === EActions.remove && isAddToVigboAlbumAvailable && !currentVigboAlbum) {
                return null;
            }

            if (
                ENABLE_FULL_RESPONSIVE &&
                windowWidth <= BREAKPOINT_SM &&
                (action === EActions.remove || action === EActions.publish || action === EActions.share)
            ) {
                return null;
            }

            if (action === EActions.createVigboGallery) {
                item.disabled = !isLinkedVigboAccount || !isVigboActiveTariff;
            }

            if (action === EActions.addToVigboAlbum) {
                item.icon = getAddToVigboAlbumIcon(isVigbo);
            }

            return {
                ...item,
                priority: getPriority(settings[action]),
            };
        })
        .filter((el) => !!el) as TToolbarItem[];

    if (
        isAiOrganizeMultiSortButton &&
        selectedCount > 1 &&
        (storage === EStorageType.home || storage === EStorageType.attaches) &&
        isAiOrganizeSortBttnInToolbar()
    ) {
        actions.splice(2, 0, { ...toolbarItems[EActions.multiSortAiOrganizeFakedoor] });
    }

    let moreListOrderFiltered = moreListOrder.filter((action) => availableActions[action] && Boolean(toolbarDropdownItems[action]));
    if (isFavorites) {
        moreListOrderFiltered = moreListOrderFiltered.filter(
            (o) => isFavorites && ![EActions.addToFavorites, EActions.removeFromFavorites].includes(o)
        );
    }
    if (isStart) {
        moreListOrderFiltered = moreListOrderFiltered.filter((action) => !toolbarStartPageItems[action]);
    }

    if (!isAddToVigboAlbumAvailable || currentVigboAlbum) {
        moreListOrderFiltered = moreListOrderFiltered.filter((action) => action !== EActions.remove);
    }

    const moreList: ListItem[] = moreListOrderFiltered.map((action) => toolbarDropdownItems[action]);

    if (moreList.length) {
        if (ENABLE_FULL_RESPONSIVE && windowWidth <= BREAKPOINT_SM) {
            if (availableActions[EActions.publish]) {
                moreList.push(toolbarItems.publish as ListItem);
            }

            if (availableActions[EActions.share]) {
                moreList.push(toolbarItems.share as ListItem);
            }
        }

        if (availableActions[EActions.makeFamilyFolder]) {
            moreList.push(toolbarDropdownItems.makeFamilyFolder);
        }

        if (availableActions[EActions.makeFamilyFolderCommon]) {
            moreList.push(toolbarDropdownItems.makeFamilyFolderCommon);
        }

        actions.push({
            ...toolbarItems[EActions.more],
            // tempexp_17340-next-line
            ...(currentFolderIsShared &&
                isThemedSharedToobarItemMore &&
                changeDesignTooltipPromo && { tooltipId: EToolbarItemTooltip.changeDesign }),
            list: moreList,
        });
    }

    return actions;
};
